import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Skeleton, Space, Timeline } from '@mantine/core';
import { RiClockwise2Line } from 'react-icons/ri';
import { PiAirplaneInFlight } from 'react-icons/pi';
import { TbCreditCardRefund } from 'react-icons/tb';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { MdOutlineFreeCancellation, MdOutlineMoneyOff, MdOutlinePending } from 'react-icons/md';

import { useFlightData } from 'libs/data-access';
import { EDrawer, EPlaceholder, ESegmentedControl } from 'libs/ui';
import { humanizeText, parseLocalDateTime, showToast } from 'utils/utility';
import { IBookingTimeline, IFlightOnlineBookingList } from 'libs/data-access/types';

interface IProps {
    flightTicket: IFlightOnlineBookingList;
    opened: boolean;
    onClose: Dispatch<SetStateAction<boolean>>;
};

export default function FlightTicketTimeline({ ...props }: IProps) {

    const { getFlightData } = useFlightData();

    const [journeySegments, setJourneySegments] = useState<{
        label: string; value: string;
    }[]>([]);
    const [timeline, setTimeline] = useState<IBookingTimeline[]>([]);
    const [selectedBooking, setSelectedBooking] = useState<{ bookingId: number; key: string; }>({ bookingId: 0, key: "" });
    const [isLoadingTimeline, setIsLoadingTimeline] = useState<boolean>(false);


    useEffect(() => {
        if (props.flightTicket?.uuid) {
            createJourneySegments();
        };
    }, [props.opened]); // props.flightTicket


    // HANDLER FUNCTIONS
    const createJourneySegments = () => {
        let segments: { label: string; value: string; }[] = [];

        props.flightTicket.bookingResponses.forEach(
            booking => {
                // for galileo journey
                booking.galileoBookingDetailResponses?.forEach(
                    galJourney => segments.push({
                        label: `${galJourney.origin} - ${galJourney.destination}`,
                        value: `${booking.id}/${galJourney.origin}_${galJourney.destination}`,
                    })
                );

                // for navitaire journey
                booking.navitaireBookingDetailResponses?.forEach(
                    navJourney => segments.push({
                        label: `${navJourney.origin} - ${navJourney.destination}`,
                        value: `${booking.id}/${navJourney.origin}_${navJourney.destination}`,
                    })
                );

                // for akbar journey
                booking.akbarBookingDetailResponses?.forEach(
                    akbarJourney => segments.push({
                        label: `${akbarJourney.origin} - ${akbarJourney.destination}`,
                        value: `${booking.id}/${akbarJourney.origin}_${akbarJourney.destination}`,
                    })
                );
            }
        );

        // console.log("segments: ", segments);
        setJourneySegments(segments);

        const bookingId = Number(segments[0].value.split("/")[0]);
        const key = segments[0].value.split("/")[1];

        setSelectedBooking({ bookingId, key });
        getTimeline({ bookingId, key });
    };


    // API CALLS
    const getTimeline = async ({ bookingId, key }: { bookingId: number; key: string; }) => {
        setIsLoadingTimeline(true);

        const response = await getFlightData(`/booking/${bookingId}/booking-status-progress?key=${key}`);

        if (response.isSuccess) {
            // console.log("Response for getting timeline: ", response.data?.data);
            setTimeline(response.data?.data);
        } else {
            console.log("Error while getting timeline: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while getting timeline!"
            });
        };

        setIsLoadingTimeline(false);
    };


    return (
        <EDrawer
            size="lg"
            opened={props.opened}
            onClose={() => props.onClose(false)}
            title={
                <div className="flex items-center gap-2">
                    <PiAirplaneInFlight size={30} />
                    <div className="text-lg font-semibold">Flight Timeline</div>
                </div>
            }
        >
            <ESegmentedControl
                fullWidth
                data={journeySegments}
                value={selectedBooking ? `${selectedBooking.bookingId}/${selectedBooking.key}` : undefined}
                onChange={(value) => {
                    const bookingId = Number(value.split("/")[0]);
                    const key = value.split("/")[1];
                    getTimeline({ bookingId, key });
                    setSelectedBooking({ bookingId, key });
                }}
            />

            <Space my={"lg"} />

            {
                isLoadingTimeline
                    ?
                    <div className="flex flex-col gap-5">
                        <Skeleton height={70} radius={"md"} />
                        <Skeleton height={70} radius={"md"} />
                        <Skeleton height={70} radius={"md"} />
                        <Skeleton height={70} radius={"md"} />
                        <Skeleton height={70} radius={"md"} />
                    </div>
                    :
                    <div>
                        {
                            !timeline.length
                                ?
                                <EPlaceholder
                                    title="No timeline data found!"
                                />
                                :
                                <Timeline bulletSize={34} lineWidth={3} radius={"md"}>
                                    {
                                        timeline.map((each, index) => (
                                            <Timeline.Item
                                                key={index + "_h_timeline"}
                                                lineVariant="solid"
                                                bullet={
                                                    each.bookingStatus === "SUCCESS"
                                                        ?
                                                        <IoIosCheckmarkCircleOutline size={22} color="green" />
                                                        :
                                                        each.bookingStatus === "FAILED"
                                                            ?
                                                            <MdOutlinePending size={22} color="red" />
                                                            :
                                                            each.bookingStatus === "PENDING"
                                                                ?
                                                                <MdOutlineMoneyOff size={22} color="orange" />
                                                                :
                                                                each.bookingStatus === "CANCELLED"
                                                                    ?
                                                                    <MdOutlineFreeCancellation size={22} color="red" />
                                                                    :
                                                                    each.bookingStatus === "TO_BE_REFUNDED"
                                                                        ?
                                                                        <TbCreditCardRefund size={22} color="orange" />
                                                                        :
                                                                        each.bookingStatus === "REFUNDED"
                                                                            ?
                                                                            <RiClockwise2Line size={22} color="violet" />
                                                                            :
                                                                            <IoIosCheckmarkCircleOutline size={22} />
                                                }
                                                title={
                                                    <div className="flex items-center gap-2">
                                                        <span
                                                            className={
                                                                `text-sm font-semibold ${each.bookingStatus === "SUCCESS"
                                                                    ?
                                                                    "text-green-600"
                                                                    :
                                                                    each.bookingStatus === "FAILED"
                                                                        ?
                                                                        "text-red-600"
                                                                        :
                                                                        each.bookingStatus === "PENDING"
                                                                            ?
                                                                            "text-teal-600"
                                                                            :
                                                                            each.bookingStatus === "CANCELLED"
                                                                                ?
                                                                                "text-red-600"
                                                                                :
                                                                                each.bookingStatus === "TO_BE_REFUNDED"
                                                                                    ?
                                                                                    "text-yellow-600"
                                                                                    :
                                                                                    each.bookingStatus === "REFUNDED"
                                                                                        ?
                                                                                        "text-violet-600"
                                                                                        :
                                                                                        "text-gray-600"
                                                                }`
                                                            }
                                                        >{humanizeText(each.bookingStatus)}</span>
                                                        <span className="text-sm">{parseLocalDateTime(each.createdAt)}</span>
                                                    </div>
                                                }
                                            >
                                                <div className="flex flex-col">
                                                    <span className="text-sm text-slate-500">{each.name}, {each.email}, ({humanizeText(each.userType)})</span>
                                                    {each.remark ? <span className="text-sm text-slate-500">Remarks: {each.remark}</span> : <></>}
                                                </div>
                                            </Timeline.Item>
                                        ))
                                    }
                                </Timeline>
                        }
                    </div>
            }
        </EDrawer>
    )
}